// Admin Panel Types

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: "super_admin" | "admin" | "moderator";
  permissions: Permission[];
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  id: string;
  name: string;
  resource: string;
  action: "create" | "read" | "update" | "delete" | "export";
  description: string;
}

export interface Device {
  id: string;
  deviceId: string;
  platform: "ios" | "android" | "web" | "desktop";
  deviceModel: string;
  deviceBrand: string;
  osVersion: string;
  appVersion: string;
  country: string;
  countryCode: string;
  isBlocked: boolean;
  suspiciousActivity: number;
  totalFaceSwaps: number;
  imageFaceSwaps: number;
  videoFaceSwaps: number;
  lastActive: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeviceAnalytics {
  totalDevices: number;
  activeDevices: number;
  blockedDevices: number;
  suspiciousDevices: number;
  platformDistribution: PlatformDistribution[];
  locationDistribution: LocationDistribution[];
  faceSwapStats: FaceSwapStats;
  recentActivity: RecentActivity[];
}

export interface PlatformDistribution {
  platform: string;
  count: number;
  percentage: number;
  [key: string]: any;
}

export interface LocationDistribution {
  country: string;
  countryCode: string;
  count: number;
  percentage: number;
}

export interface FaceSwapStats {
  totalFaceSwaps: number;
  imageFaceSwaps: number;
  videoFaceSwaps: number;
  dailyFaceSwaps: number;
  weeklyFaceSwaps: number;
  monthlyFaceSwaps: number;
  averagePerDevice: number;
  averageImageFaceSwaps?: number;
  averageVideoFaceSwaps?: number;
}

export interface RecentActivity {
  id: string;
  type: "device_registered" | "face_swap" | "device_blocked" | "admin_login";
  deviceId?: string;
  adminId?: string;
  description: string;
  timestamp: string;
  metadata?: any;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  role: "admin" | "moderator";
  permissions: string[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
  details?: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  documents: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    limit: number;
  };
}

export interface UsersResponse {
  users: AdminUser[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    limit: number;
  };
}

export interface FilterOptions {
  search?: string;
  platform?: string;
  country?: string;
  isBlocked?: boolean;
  isActive?: boolean;
  dateFrom?: string;
  dateTo?: string;
  role?: string;
  signInMethod?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface SortOptions {
  field: string;
  order: "asc" | "desc";
}

export interface ExportOptions {
  format: "csv" | "excel" | "pdf";
  fields: string[];
  filters?: FilterOptions;
  filename?: string;
}

export interface DashboardStats {
  totalUsers: number;
  totalDevices: number;
  totalFaceSwaps: number;
  imageFaceSwaps: number;
  videoFaceSwaps: number;
  recentImageFaceSwaps?: number;
  recentVideoFaceSwaps?: number;
  activeSessions: number;
  systemHealth: "healthy" | "warning" | "critical";
  uptime: number;
  lastBackup?: string;
}

export interface SystemConfig {
  id: string;
  key: string;
  value: any;
  type: "string" | "number" | "boolean" | "json";
  description: string;
  isEditable: boolean;
  updatedAt: string;
  updatedBy: string;
}

export interface AuditLog {
  id: string;
  adminId: string;
  adminName: string;
  action: string;
  resource: string;
  resourceId?: string;
  details: any;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}

export interface HtmlContent {
  id: string;
  _id?: string; // MongoDB _id
  type: "contact_us" | "privacy_policy" | "terms_conditions";
  title: string | Record<string, string>; // Can be string (legacy) or multilingual object
  content: string | Record<string, string>; // Can be string (legacy) or multilingual object
  isActive: boolean;
  lastUpdatedBy: {
    id: string;
    name: string;
    email: string;
  };
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface HtmlContentFormData {
  type: "contact_us" | "privacy_policy" | "terms_conditions";
  title: string | Record<string, string>; // Can be string (legacy) or multilingual object
  content: string | Record<string, string>; // Can be string (legacy) or multilingual object
}

export interface TemplateImage {
  _id: string;
  imageUrl: string;
  imageName: string;
  fileType?: "image" | "video" | "gif";
  mimeType?: string;
  fileSize?: number;
  order: number;
  isPremium?: boolean;
  isGroupImage?: boolean;
  uploadedAt: string;
}

// Platforms a template category can be hidden on (matched against the app's X-Platform header).
export type CategoryPlatform = 'ios' | 'android' | 'web';
export const CATEGORY_PLATFORM_LABELS: Record<CategoryPlatform, string> = { ios: 'iOS', android: 'Android', web: 'Web' };

export interface Category {
  _id: string;
  name: string | Record<string, string>; // Can be string (legacy) or multilingual object
  slug: string;
  description: string | Record<string, string>; // Can be string (legacy) or multilingual object
  templateImages: TemplateImage[];
  isActive: boolean;
  displayOrder: number;
  imagesPerRow?: number; // 3 or 4, default 3
  hiddenPlatforms?: CategoryPlatform[]; // platforms the category is hidden on
  icon: string;
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryFormData {
  name: string | Record<string, string>; // Can be string (legacy) or multilingual object
  description: string | Record<string, string>; // Can be string (legacy) or multilingual object
  isActive: boolean;
  displayOrder: number;
  imagesPerRow?: number; // 3 or 4, default 3
  hiddenPlatforms?: CategoryPlatform[];
  icon: string;
}

// Creative Look Templates
export interface CreativeLookTemplate {
  _id: string;
  name: string;
  description: string;
  promptText: string; // AI prompt (visible in admin only)
  referenceImageUrl?: string;
  category: "formal" | "casual" | "traditional" | "fantasy" | "professional" | "seasonal" | "artistic" | "historical" | "futuristic" | "other";
  tags: string[];
  credits: number;
  likes: number;
  uses: number;
  displayOrder: number;
  status: "active" | "inactive" | "draft";
  createdAt: string;
  updatedAt: string;
}

export interface CreativeLookTemplateFormData {
  name: string;
  description: string;
  promptText: string;
  category: string;
  tags: string[];
  credits: number;
  displayOrder: number;
  status: "active" | "inactive" | "draft";
}

// Admin dashboard daily charts (GET /api/admin/dashboard/daily)
export interface DashboardDailyPoint {
  date: string; // YYYY-MM-DD in the dashboard timezone
  devices: number;
  signups: number;
  generations: number;
  purchases: number;
  revenue: number; // USD purchases only
  nonUsdPurchases: number;
}

export interface DashboardDaily {
  timezone: string;
  days: number;
  series: DashboardDailyPoint[];
}
